// RgbLed.js - Luzes RGB da sala, via Tuya
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb, Power, AlertCircle } from 'lucide-react';
import { useTuya, hexToHsv, hsvToHex } from '../hooks/useTuya';
import ViewHeader from './ViewHeader';

function RgbLed({ onBack }) {
  const { t } = useTranslation();
  const { devices, loading, error, fetchDevices, control } = useTuya();

  const luz = devices.find((d) => d.device_type === 'rgb_led');
  const corAtual = luz ? hsvToHex(luz.hue, luz.saturation, luz.value) : '#ffffff';

  const alternarLuz = () => control(luz.device_id, { switch_led: !luz.power });
  const definirBrilho = (valor) => control(luz.device_id, { bright_value: parseInt(valor, 10) });

  const definirCor = (hex) => {
    const { h, s, v } = hexToHsv(hex);
    // Um valor de 0 apagaria a luz sem que o interruptor mudasse de estado,
    // o que confundia: escolhia-se preto e a luz "desligava-se" sozinha.
    control(luz.device_id, { work_mode: 'colour', colour_data: { h, s, v: Math.max(v, 100) } });
  };

  return (
    <div className="app-container">
      <div className="app-content">
        <ViewHeader
          icon={Lightbulb}
          iconClass="icon-led"
          titulo={t('led.titulo')}
          subtitulo={t('led.subtitulo')}
          onBack={onBack}
          onRefresh={fetchDevices}
          loading={loading}
        />

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {!luz ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>{t('led.aCarregar')}</p>
          </div>
        ) : (
          <div className="heatpump-container">
            <div className={`heatpump-status-card ${luz.online ? 'online' : 'offline'}`}>
              <div className="heatpump-status-header">
                <div className="status-indicator">
                  <div className={`status-dot ${luz.online ? 'online' : 'offline'}`} />
                  <span>{luz.online ? t('comum.online') : t('comum.offline')}</span>
                </div>
                <div className={`power-badge ${luz.power ? 'on' : 'off'}`}>
                  {luz.power ? t('comum.ligadoMaiusc') : t('comum.desligadoMaiusc')}
                </div>
              </div>

              <div className="control-section">
                <button
                  className={`btn-power ${luz.power ? 'on' : 'off'}`}
                  onClick={alternarLuz}
                  disabled={loading || !luz.online}
                >
                  <Power size={24} />
                  <span>{luz.power ? t('comum.desligar') : t('comum.ligar')}</span>
                </button>
              </div>
            </div>

            <div className="heatpump-controls-card">
              <h3>{t('led.cor')}</h3>
              <div className="control-section">
                <input
                  type="color"
                  value={corAtual}
                  onChange={(e) => definirCor(e.target.value)}
                  disabled={loading || !luz.online || !luz.power}
                  className="led-color-input"
                />
              </div>

              <h3>{t('led.brilho')}</h3>
              <div className="control-section">
                <input
                  type="range"
                  min={10}
                  max={1000}
                  value={luz.brightness}
                  onChange={(e) => definirBrilho(e.target.value)}
                  className="temp-slider"
                  disabled={loading || !luz.online || !luz.power}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RgbLed;
